import { createApp } from 'vue';
import { IonicVue } from '@ionic/vue';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

/* @import '@ionic/vue/css/palettes/dark.always.css'; */
/* @import '@ionic/vue/css/palettes/dark.class.css'; */
// import '@ionic/vue/css/palettes/dark.system.css';

/* Theme variables */
import './theme/main.css';
import { createPinia } from 'pinia';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
// @ts-expect-error vue-virtual-scroller has no type declarations
import { RecycleScroller } from 'vue-virtual-scroller';
import App from './App.vue';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useAuth } from './stores/auth.store';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { SplashScreen } from '@capacitor/splash-screen';
import { vAuth, vAuthElse } from './directives/auth';
import SentryUtil from './utils/sentry';
import { createSentryPiniaPlugin } from '@sentry/vue';
import { vSuperAdmin } from './directives/super-admin';
import { InitRouter } from './router';

defineCustomElements(window);
const pinia = createPinia();

const app = createApp(App);
app.component('RecycleScroller', RecycleScroller);
app.use(IonicVue, { mode: 'ios', swipeBackEnabled: true });
app.use(pinia);
pinia.use(createSentryPiniaPlugin());
app.directive('super-admin', vSuperAdmin);
app.directive('auth', vAuth);
app.directive('auth-else', vAuthElse);
axios.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const auth = useAuth();
    if (err.response?.status === 401 && !err.config?.headers?.['x-no-retry'] && auth.token) {
      console.log('ATTEMPTING TO REFRESH TOKEN');
      const refresh = await auth.refresh();
      if (refresh.success) {
        const originalReq = err.config as AxiosRequestConfig;
        originalReq.headers = {
          ...(originalReq.headers || {}),
          'x-no-retry': 'true',
          authorization: refresh.token,
        };
        return axios(originalReq);
      } else {
        auth.logout();
      }
    } else if (err.response?.status === 401) {
      auth.logout();
    }
    return Promise.reject(err);
  }
);

const router = InitRouter();
SentryUtil.init(app, router)
  .catch((err) => {
    console.error('[SENTRY] initialization failed', JSON.stringify(err));
  })
  .finally(() => {
    console.log('[SENTRY] Initialization finished');
    app.use(router);
    router.isReady().then(() => {
      app.mount('#app');
      SplashScreen.hide();
    });
  });
